import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Skeleton } from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

/**
 * SavedItemsGrid — a 3-column preview grid of saved / reviewer items.
 *
 * Reveals PAGE items at a time and loads the next PAGE when the sentinel at
 * the bottom scrolls into view (client-side infinite scroll). Handles both
 * the saved-item shape (item_name / item_image_url) and the reviewer/place
 * shape (event_name / image_url / …) so a single grid backs both contexts.
 */

const PAGE = 9;

const getName = (it) =>
  it.item_name || it.event_name || it.place_name || it.venue_name || it.name || it.title || '';

const getImage = (it) =>
  it.item_image_url ||
  it.generated_image_url ||
  it.image ||
  it.image_url ||
  it.cover_image_url ||
  it.poster_url ||
  it.event_image_link ||
  it.main_image_url ||
  (it.photo && it.photo.images && it.photo.images.large && it.photo.images.large.url) ||
  it.thumbnail_url ||
  null;

const getKey = (it, i) =>
  `${it.item_type || it._source || 'x'}-${it.item_id || it.event_id || it.id || i}`;

const SavedItemsGrid = ({ items = [], loading = false, language = 'TH', onItemClick, emptyText }) => {
  const [visible, setVisible] = useState(PAGE);
  const sentinelRef = useRef(null);

  // Reset the window whenever the source list changes (e.g. switching reviewer)
  useEffect(() => { setVisible(PAGE); }, [items]);

  const shown = useMemo(() => items.slice(0, visible), [items, visible]);
  const hasMore = visible < items.length;

  const loadMore = useCallback(() => {
    setVisible((v) => Math.min(v + PAGE, items.length));
  }, [items.length]);

  useEffect(() => {
    if (!hasMore) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '120px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore, shown.length]);

  if (loading && items.length === 0) {
    return (
      <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        {Array.from({ length: 9 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" sx={{ width: '100%', aspectRatio: '1 / 1', borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, p: 4, textAlign: 'center' }}>
        <BookmarkBorderIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
          {emptyText || (language === 'TH' ? 'ยังไม่มีรายการที่บันทึกไว้' : language === 'CN' ? '暂无已保存项目' : 'No saved items yet')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        {shown.map((it, i) => {
          const name = getName(it);
          const img = getImage(it);
          return (
            <Box
              key={getKey(it, i)}
              onClick={() => onItemClick && onItemClick(it)}
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: onItemClick ? 'pointer' : 'default',
                aspectRatio: '1 / 1',
                backgroundColor: '#f3f4f6',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': onItemClick ? { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' } : {},
              }}
            >
              {img ? (
                <Box
                  component="img"
                  src={img}
                  alt={name}
                  loading="lazy"
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                  <BookmarkBorderIcon sx={{ color: '#9ca3af' }} />
                </Box>
              )}
              <Box sx={{
                position: 'absolute', left: 0, right: 0, bottom: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 100%)',
                p: 0.75, pt: 2,
              }}>
                <Typography sx={{
                  color: '#fff', fontSize: '0.68rem', fontWeight: 700, lineHeight: 1.2,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {name || '—'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {hasMore && (
        <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} sx={{ color: '#7c3aed' }} />
        </Box>
      )}
    </Box>
  );
};

export default SavedItemsGrid;
